import {
  Button,
  H5,
  H6,
  RadioSelector,
  RadioSelectorOption,
} from "@sellar-io/components";
import { SpinnerLegacy } from "../../../../components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { useOrder } from "../../../../hooks/api/v2/order/useOrder";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { usePaymentList } from "../../../../hooks/api/v2/payment/usePaymentList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { stripeElementsAppearance } from "./stripeElementsAppearance";
import { ReactElement, useState } from "react";
import { usePaymentIntent } from "../../../../hooks/api/v2/paymentIntent/usePaymentIntent";
import { usePaymentIntentCreate } from "../../../../hooks/api/v2/paymentIntent/usePaymentIntentCreate";
import { Alert } from "../../../../components/alert/Alert";
import {
  calculateCardPaymentDeposit,
  capitalize,
  formatCurrency,
  sanitizeEmailArray,
} from "@sellar-io/common";
import { usePaymentMethodList } from "../../../../hooks/api/v2/paymentMethod/usePaymentMethodList";
import { twMerge } from "tailwind-merge";
import { useLocation } from "react-router";

type PaymentFailedDialogProps = {
  orderId: number;
  active: boolean;
  onDismiss: () => void;
};

export const CardPaymentWithDepositFailedDialog = ({
  orderId,
  active,
  onDismiss,
}: PaymentFailedDialogProps) => {
  if (!orderId) throw new Error("No order Id found");
  const orderQuery = useOrder(orderId);
  const order = orderQuery.data;

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        title={"Choose payment card"}
      />
      {orderQuery.isPending ? (
        <SpinnerLegacy />
      ) : (
        <>
          {order && (
            <CardPaymentWithDepositFailedDialogContentContainer
              order={order}
              onDismiss={onDismiss}
            />
          )}
        </>
      )}
    </Dialog>
  );
};

const CardPaymentWithDepositFailedDialogContentContainer = ({
  onDismiss,
  order,
}: {
  order: OrderGetResponseType;
  onDismiss: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  if (!order) throw new Error("No order found");

  const paymentsQuery = usePaymentList(
    {
      query: [
        {
          type: "OrderId",
          modifier: "EQUALS",
          value: order.id,
        },
      ],
      limit: 1,
    },
    {
      enabled:
        order.paymentType === "cardPaymentWithDeposit" && !!business?.Retailer,
    },
  );

  const stripePromise = loadStripe(
    import.meta.env.VITE_STRIPE_PUBLIC_KEY || "",
    {
      stripeAccount: order.Supplier.stripeAccountId ?? undefined,
    },
  );

  const loading = paymentsQuery.isPending;
  const payment = paymentsQuery.data?.pages[0]?.[0];

  if (loading) {
    return (
      <DialogContent>
        <SpinnerLegacy />
      </DialogContent>
    );
  }

  const depositAmount = calculateCardPaymentDeposit(order.price);

  return (
    <DialogContent>
      <Elements
        stripe={stripePromise}
        options={{
          mode: "payment",
          currency: "gbp",
          amount: depositAmount,
          setup_future_usage: "on_session",
          allowedPaymentMethodTypes: ["card"],
          appearance: stripeElementsAppearance,
        }}
      >
        {loading ? (
          <SpinnerLegacy />
        ) : (
          <>
            <CardPaymentWithDepositFailedDialogContent
              onDismiss={onDismiss}
              supplierId={order.SupplierId}
              orderId={order.id}
              price={depositAmount}
              stripePaymentIntentId={payment?.paymentIntentId}
            />
          </>
        )}
      </Elements>
    </DialogContent>
  );
};

const CardPaymentWithDepositFailedDialogContent = ({
  stripePaymentIntentId,
  orderId,
  price,
  supplierId,
}: {
  stripePaymentIntentId: string | undefined;
  price: number;
  orderId: number;
  supplierId: number;
  onDismiss: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  const [updatedStripePaymentMethodId, setUpdatedStripePaymentMethodId] =
    useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const paymentIntentCreateMutation = usePaymentIntentCreate();

  // an existing failed/pending deposit payment intent is reused for the
  // retry - if there is no existing payment for this order at all, a new
  // deposit payment intent is created on submit instead (see onSubmit)

  const paymentIntentQuery = usePaymentIntent(stripePaymentIntentId ?? "", {
    enabled: !!stripePaymentIntentId,
  });

  const isPaymentIntentQueryLoading =
    paymentIntentQuery.isPending && !!stripePaymentIntentId;

  const confirmStripePayment = async ({
    clientSecret,
    paymentMethodId,
  }: {
    clientSecret: string;
    paymentMethodId: string;
  }) => {
    if (!stripe) {
      return;
    }

    const receiptEmail = sanitizeEmailArray([
      ...(business?.invoiceEmails ?? []),
      ...(business?.email ? [business.email] : []),
    ])[0];

    const { error: stripeConfirmError } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: paymentMethodId,
        receipt_email: receiptEmail,
        return_url: `${import.meta.env.VITE_URL}${location.pathname}${location.search}${location.hash}`,
      },
    );

    if (stripeConfirmError) {
      setErrorMessage(`Error creating payment: ${stripeConfirmError.message}`);
      setLoading(false);
      return;
    }

    // if successful, refresh page

    window.location.reload();
  };

  const onSubmit = async () => {
    setLoading(true);
    setErrorMessage(undefined);

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      setErrorMessage(
        "Stripe components are still loading. Please try again shortly.",
      );
      setLoading(false);
      return;
    }

    // validate elements

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setLoading(false);
      return;
    }

    // if new card is used, create payment method with card

    const cardElement = elements.getElement("card");
    const createPaymentMethodResponse = cardElement
      ? await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
        })
      : null;

    if (createPaymentMethodResponse?.error) {
      setErrorMessage(createPaymentMethodResponse.error.message ?? "");
      setLoading(false);
      return;
    }

    const paymentMethodId =
      createPaymentMethodResponse?.paymentMethod.id ??
      updatedStripePaymentMethodId;

    if (!paymentMethodId) {
      setErrorMessage("Please enter a new card or select an existing card.");
      setLoading(false);
      return;
    }

    // get payment intent stripe client secret - reuse the existing deposit
    // payment intent if one exists, otherwise create a new one (this is the
    // case where the deposit was never successfully charged in the first
    // place, so there is no existing payment/payment intent to retry)

    const clientSecret = stripePaymentIntentId
      ? paymentIntentQuery.data?.clientSecret
      : await paymentIntentCreateMutation
          .mutateAsync({
            orderId,
            orderPaymentType: "cardDeposit",
            stripePaymentMethodId: paymentMethodId,
          })
          .then((data) => data.clientSecret)
          .catch(() => undefined);

    if (!clientSecret) {
      setLoading(false);
      return setErrorMessage("An error has occurred. Please contact support.");
    }

    // confirm stripe payment

    return confirmStripePayment({ clientSecret, paymentMethodId });
  };

  return (
    <>
      {errorMessage && (
        <Alert
          message={errorMessage}
          type={"error"}
          className={"mb-2 w-full"}
        />
      )}
      <PaymentStripeCardSelection
        supplierId={supplierId}
        value={updatedStripePaymentMethodId}
        onChange={(e) => setUpdatedStripePaymentMethodId(e)}
      />
      <DialogCTA>
        <Button
          disabled={isPaymentIntentQueryLoading || loading}
          loading={isPaymentIntentQueryLoading || loading}
          className={"w-full"}
          onClick={async () => await onSubmit()}
        >
          Pay {formatCurrency(price)}
        </Button>
      </DialogCTA>
    </>
  );
};

const PaymentStripeCardSelection = ({
  supplierId,
  onChange,
  value,
}: {
  supplierId: number;
  value: string | undefined;
  onChange: (paymentMethodId: string | undefined) => void;
}): ReactElement => {
  const query = usePaymentMethodList({
    query: [
      {
        value: supplierId,
        type: "SupplierId",
        modifier: "EQUALS",
      },
    ],
  });

  if (query.isPending) return <SpinnerLegacy />;
  const cards = query.data?.pages[0];
  if (!value && value !== "") {
    onChange(cards?.[0]?.Suppliers[0]?.stripePaymentMethodId ?? undefined);
  }

  return (
    <>
      {cards?.length ? (
        <>
          <RadioSelector
            onChange={(e) => onChange(e.currentTarget.value)}
            value={value ?? ""}
          >
            {cards.map((card) => (
              <RadioSelectorOption
                key={card.Suppliers[0]?.stripePaymentMethodId}
                value={card.Suppliers[0]?.stripePaymentMethodId ?? ""}
              >
                <div className="flex flex-row justify-between">
                  <div className="flex flex-row gap-2">
                    <H5>{capitalize(card.brand ?? "")} Debit</H5>
                    <H6>Ending in {card.last4}</H6>
                  </div>
                </div>
              </RadioSelectorOption>
            ))}
            <RadioSelectorOption value={"new-card"}>
              <H5>Add new payment card</H5>
              {value === "new-card" && (
                <PaymentStripeCreateCard className={"mt-4"} />
              )}
            </RadioSelectorOption>
          </RadioSelector>
        </>
      ) : (
        <RadioSelector
          onChange={(e) => onChange(e.currentTarget.value)}
          value={value ?? ""}
        >
          <RadioSelectorOption value={"new-card"}>
            <H5>Add new payment card</H5>
            <PaymentStripeCreateCard className={"mt-4"} />
          </RadioSelectorOption>
        </RadioSelector>
      )}
    </>
  );
};

const PaymentStripeCreateCard = ({
  className,
}: {
  className?: string;
}): ReactElement => {
  return (
    <div className={twMerge(className)}>
      <CardElement options={{ hidePostalCode: true }} />
    </div>
  );
};
